import { Track } from "@/src/lib/tracking";
import {
  getAnalytics,
  initializeAnalytics,
  logEvent,
  type Analytics,
} from "firebase/analytics";
import { FirebaseApp, getApp, getApps, initializeApp } from "firebase/app";

import {
  fetchAndActivate,
  getRemoteConfig,
  RemoteConfig,
  getValue as getFirebaseValue,
} from "firebase/remote-config";
import { config } from "@config/config";

const firebaseConfig = config?.firebaseConfig;

const getFirebaseApp = () => {
  return getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
};

const genRemoteConfig = () => {
  let remoteConfig: RemoteConfig;

  return (app?: FirebaseApp) => {
    if (remoteConfig) {
      return remoteConfig;
    }
    remoteConfig = getRemoteConfig(app);
    return remoteConfig;
  };
};

const getFirebaseConfig = genRemoteConfig();
const fireBaseApp = getFirebaseApp();

export const initFirebaseRemoteConfig = async () => {
  const app = getFirebaseApp();
  const remoteConfig = getFirebaseConfig(app);
  remoteConfig.settings.minimumFetchIntervalMillis = 1;
  remoteConfig.defaultConfig = {};
  await fetchAndActivate(remoteConfig);
};

export const getValue = async (key: string) => {
  const remoteConfig = getFirebaseConfig();
  await fetchAndActivate(remoteConfig);

  return getFirebaseValue(remoteConfig, key);
};

/**
 * Analytics 单例 —— **必须走 `initializeAnalytics` 且关掉 `send_page_view`**。
 *
 * 背景（2026-09-10 实测）：本模块的 analytics 是**懒初始化**的（只有存量 `trackEvent` 落点
 * 被命中时才走到这里），而 Firebase JS SDK 在首次 `getAnalytics()` 时会
 * `gtag('config', measurementId, …)`，**默认副作用就是补发一条 `page_view`**
 * （见 `@firebase/analytics` 的 `_initializeAnalytics`：
 * "This will trigger a page_view event unless 'send_page_view' is set to false"）。
 *
 * 那条 `page_view` 是**假的**：它不对应任何一次页面加载，而是「用户第一次触发某个存量埋点」
 * 的时刻。它会往 `measurementId`（prod = `G-1LJC5P2LD7`，Firebase web 应用那条流）里
 * 凭空塞进页面浏览与半截会话，把该流的会话数抬高、平均互动时长摊薄。
 * 生产站真正承载页面浏览的是 GTM 容器下发的另一条流（`GTM-5TQNBQGS` → `G-D517P0FSVX` /
 * `G-0RHCTHJEEF`），这里不该也不需要再造一份。
 *
 * 关掉之后：`edgen_*` 事件照发，只是不再伪造 page_view。
 */
let analyticsInstance: Analytics | null = null;

function getAnalyticsSafely(): Analytics | null {
  if (analyticsInstance) {
    return analyticsInstance;
  }
  try {
    analyticsInstance = initializeAnalytics(fireBaseApp, {
      config: { send_page_view: false },
    });
  } catch {
    // 已被别处初始化过（initializeAnalytics 不可重复调用）→ 退回取现成实例；
    // 不支持的环境（SSR / 无 IndexedDB）则整体放弃，不要把异常抛给调用方。
    try {
      analyticsInstance = getAnalytics(fireBaseApp);
    } catch (e: any) {
      Track.log("warn", "initFirebase", `--getAnalytics--${e?.message}`);
      return null;
    }
  }
  return analyticsInstance;
}

export function logEventAnalytics(
  eventName: string,
  params?: { [key: string]: any },
) {
  // ⚠️ `getAnalytics` 原先写在 try 之外，不支持的环境会把异常抛给调用方（40 个存量落点全受影响）
  const analytics = getAnalyticsSafely();
  if (!analytics) {
    return;
  }

  try {
    // 处理params，将对象转换为字符串
    const processedParams = params ? processAnalyticsParams(params) : undefined;
    return logEvent(analytics, `edgen_${eventName}`, processedParams);
  } catch (e: any) {
    Track.log("error", "initFirebase", `--logEventAnalytics--${e?.message}`);
  }
}

// 递归处理参数，将对象转换为JSON字符串
function processAnalyticsParams(params: { [key: string]: any }): {
  [key: string]: any;
} {
  const result: { [key: string]: any } = {};

  for (const key in params) {
    if (params.hasOwnProperty(key)) {
      const value = params[key];

      if (value === null) {
        result[key] = "null";
      } else if (value === undefined) {
        // 跳过undefined值
        continue;
      } else if (typeof value === "object" && !Array.isArray(value)) {
        // 对象转为JSON字符串
        try {
          result[key] = JSON.stringify(value);
        } catch (error) {
          result[key] = "[Object cannot be serialized]";
          Track.log(
            "warn",
            "initFirebase",
            `Analytics object serialization failed: ${key}`,
            { cause: String(error) },
          );
        }
      } else if (Array.isArray(value)) {
        // 数组也转为字符串
        try {
          result[key] = JSON.stringify(value);
        } catch (error) {
          result[key] = "[Array cannot be serialized]";
          Track.log(
            "warn",
            "initFirebase",
            `Analytics array serialization failed: ${key}`,
            { cause: String(error) },
          );
        }
      } else {
        // 原始类型直接保留
        result[key] = value;
      }
    }
  }

  return result;
}
